import { StatusPill, type StatusPillVariant } from '../../components/StatusPill'
import { ForwardLookingCell } from '../../design-system/list-view/cells/ForwardLookingCell'
import type { ColumnDef } from '../../design-system/list-view/types'
import { label } from '../../lib/labels'
import type { ProgramFilingListView } from '../../types/programFiling'

const FY_GAP =
  'No FiscalYear get-by-id read path yet (docs/design/IB-53-film-ui-design.md §7 Q2) — flagged to Prabhu/Tech Lead.'

const STATE_VARIANT: Record<string, StatusPillVariant> = {
  DRAFT: 'neutral',
  IN_PREPARATION: 'info',
  SUBMITTED: 'info',
  UNDER_REVIEW: 'warning',
  CERTIFIED: 'success',
  APPROVED: 'success',
  REJECTED: 'danger',
  WITHDRAWN: 'muted',
}

/**
 * CLAUDE.md §4 — every cross-case list carries Account · Legal entity ·
 * Case · Fiscal year, in that order, for sort/filter/refine.
 */
export function buildProgramFilingColumns(): ColumnDef<ProgramFilingListView>[] {
  return [
    {
      id: 'accountName',
      header: 'Account',
      width: 200,
      minWidth: 160,
      sortable: false,
      pinnable: true,
      toExportText: (row) => row.accountName ?? '',
      render: (row) => row.accountName ?? <span style={{ color: 'var(--iw-text-subtle)' }}>—</span>,
    },
    {
      id: 'legalEntityName',
      header: 'Legal entity',
      width: 200,
      sortable: false,
      toExportText: (row) => row.legalEntityName ?? '',
      render: (row) => row.legalEntityName ?? <span style={{ color: 'var(--iw-text-subtle)' }}>—</span>,
    },
    {
      id: 'caseName',
      header: 'Case',
      width: 180,
      sortable: false,
      // Named gap (backend ProgramFilingListView javadoc, mirrors CaseListView.caseTitle): null for
      // an 'rnd'-kind Case (Case itself carries no title/name field) — never a fabricated value.
      toExportText: (row) => row.caseName ?? '',
      render: (row) => row.caseName ?? <span style={{ color: 'var(--iw-text-subtle)' }}>—</span>,
    },
    {
      id: 'fiscalYearLabel',
      header: 'Fiscal year',
      width: 110,
      sortable: false,
      headerInfo: { label: 'Fiscal year column info', content: FY_GAP },
      toExportText: (row) => row.fiscalYearLabel ?? '',
      render: (row) => (row.fiscalYearId ? row.fiscalYearLabel ?? <ForwardLookingCell reason={FY_GAP} /> : <ForwardLookingCell reason={FY_GAP} />),
    },
    {
      id: 'programName',
      header: 'Program',
      width: 160,
      sortable: true,
      toExportText: (row) => row.programName ?? '',
      render: (row) => row.programName ?? <span style={{ color: 'var(--iw-text-subtle)' }}>—</span>,
    },
    {
      id: 'jurisdiction',
      header: 'Jurisdiction',
      width: 120,
      sortable: false,
      toExportText: (row) => (row.jurisdiction ? label('jurisdiction', row.jurisdiction) : ''),
      render: (row) => (row.jurisdiction ? label('jurisdiction', row.jurisdiction) : <span style={{ color: 'var(--iw-text-subtle)' }}>—</span>),
    },
    {
      id: 'currentState',
      header: 'Status',
      width: 140,
      sortable: true,
      toExportText: (row) => label('programFilingState', row.currentState),
      render: (row) => (
        <StatusPill variant={STATE_VARIANT[row.currentState] ?? 'neutral'} label={label('programFilingState', row.currentState)} />
      ),
    },
    {
      id: 'submissionOutcome',
      header: 'Outcome',
      width: 130,
      sortable: false,
      defaultHidden: true,
      toExportText: (row) => (row.submissionOutcome ? label('submissionOutcome', row.submissionOutcome) : ''),
      render: (row) =>
        row.submissionOutcome ? (
          label('submissionOutcome', row.submissionOutcome)
        ) : (
          <span style={{ color: 'var(--iw-text-subtle)' }}>—</span>
        ),
    },
  ]
}

export const PROGRAM_FILING_COLUMN_ORDER = [
  'accountName',
  'legalEntityName',
  'caseName',
  'fiscalYearLabel',
  'programName',
  'jurisdiction',
  'currentState',
  'submissionOutcome',
]
